/* =========================================================================
   <video-playlist> — playback engine for sections/video-playlist.liquid.

   A custom element rather than a theme.sections.register() call, because:
   - It upgrades itself whenever the theme editor injects new markup on
     shopify:section:load. No registration, no reinit, no leaked instances.
   - theme.sections is constructed inside theme.js's own init, so registering
     from an external file would race it.
   - disconnectedCallback gives us teardown for free on section unload.

   Deliberately does NOT use theme.YouTube: that wrapper loops on end, force-
   mutes, and re-plays on every viewport entry — all three fight a playlist.
   We reuse only theme.LibraryLoader + the youTubeReady event.
   ========================================================================= */
(function () {
  'use strict';

  var YT_PLAYER_VARS = {
    autoplay: 1,
    rel: 0,
    modestbranding: 1,
    playsinline: 1,
    controls: 1
  };

  /* theme.js owns window.onYouTubeIframeAPIReady (it dispatches `youTubeReady`).
     Never define it here — doing so would silently break every other YouTube
     video on the site. If theme.js is absent we inject the SDK and poll instead. */
  function whenYouTubeReady(callback) {
    if (window.YT && window.YT.Player) {
      callback();
      return;
    }

    var theme = window.theme;

    if (theme && theme.LibraryLoader && theme.config) {
      if (theme.config.youTubeReady) {
        callback();
        return;
      }
      document.addEventListener('youTubeReady', callback, { once: true });
      theme.LibraryLoader.load('youtubeSdk');
      return;
    }

    // Fallback: no theme.js. Inject the SDK ourselves and poll for YT.Player,
    // rather than claiming the global callback theme.js expects to own.
    if (!document.getElementById('youtube-sdk')) {
      var tag = document.createElement('script');
      tag.id = 'youtube-sdk';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    var poll = setInterval(function () {
      if (window.YT && window.YT.Player) {
        clearInterval(poll);
        callback();
      }
    }, 50);
  }

  class VideoPlaylistEl extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-vpl-stage]');
      this.mount = this.querySelector('[data-vpl-mount]');
      this.posterBtn = this.querySelector('[data-vpl-poster]');
      this.posterImg = this.querySelector('[data-vpl-poster-img]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-vpl-item]'));

      if (!this.stage || !this.mount || !this.items.length) return;

      this.active = 0;
      this.ytPlayer = null;
      this.video = null;

      this.onPosterClick = this.play.bind(this);
      this.posterBtn.addEventListener('click', this.onPosterClick);

      this.onRailClick = this.handleRailClick.bind(this);
      this.addEventListener('click', this.onRailClick);

      // Theme editor: sync the stage when a merchant clicks a block in the sidebar.
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.stop();
      if (this.posterBtn) this.posterBtn.removeEventListener('click', this.onPosterClick);
      this.removeEventListener('click', this.onRailClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    handleRailClick(evt) {
      var btn = evt.target.closest('[data-vpl-item]');
      if (!btn || !this.contains(btn)) return;
      // A rail click is unambiguous play intent — swap and play, no second click.
      this.select(parseInt(btn.dataset.index, 10), true);
    }

    handleBlockSelect(evt) {
      var btn = evt.target && evt.target.querySelector
        ? evt.target.querySelector('[data-vpl-item]')
        : null;
      if (!btn || !this.contains(btn)) return;
      // Never autoplay here: a merchant clicking through blocks would otherwise
      // get a video blaring on every click.
      this.select(parseInt(btn.dataset.index, 10), false);
    }

    select(index, play) {
      if (isNaN(index) || !this.items[index]) return;

      if (index === this.active && play) {
        this.play();
        return;
      }

      this.stop();
      this.active = index;

      var btn = this.items[index];

      this.items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.setPoster(btn);

      if (this.posterBtn) {
        this.posterBtn.setAttribute('aria-label', 'Play ' + (btn.dataset.title || 'video'));
      }

      btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      if (play) this.play();
    }

    setPoster(btn) {
      if (!this.posterImg) return;
      var img = this.posterImg;
      var fallbacks = (btn.dataset.posterFallbacks || '').split('|').filter(Boolean);

      function next() {
        if (!fallbacks.length) return false;
        img.src = fallbacks.shift();
        return true;
      }

      /* When a YouTube still does not exist, i.ytimg.com answers 404 with a
         120x90 grey JPEG body, which the browser decodes as a successful load.
         So onerror never fires — the placeholder's size is the only tell. */
      img.onload = function () {
        if (img.naturalWidth <= 120) next();
      };
      img.onerror = function () {
        next();
      };
      img.src = btn.dataset.poster || '';
    }

    play() {
      var btn = this.items[this.active];
      if (!btn) return;

      this.stop();
      this.stage.classList.add('is-playing');

      if (btn.dataset.kind === 'native') {
        this.playNative();
      } else {
        this.playYouTube(btn.dataset.ytId, btn.dataset.title);
      }
    }

    playNative() {
      var tpl = this.querySelector('[data-vpl-media][data-index="' + this.active + '"]');
      if (!tpl) return;

      this.mount.appendChild(tpl.content.cloneNode(true));
      var video = this.mount.querySelector('video');
      if (!video) return;

      // video_tag only emits `playsinline` when autoplay is true, and we pass
      // autoplay: false — without this iOS Safari force-fullscreens on play().
      video.playsInline = true;
      video.controls = true;
      video.addEventListener('ended', this.stop.bind(this));

      this.video = video;
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }

    playYouTube(videoId, title) {
      if (!videoId) return;

      var host = document.createElement('div');
      this.mount.appendChild(host);

      var self = this;
      whenYouTubeReady(function () {
        // The element may have been swapped out (editor re-render) while the
        // SDK was loading.
        if (!self.isConnected || !host.isConnected) return;

        self.ytPlayer = new YT.Player(host, {
          videoId: videoId,
          playerVars: YT_PLAYER_VARS,
          events: {
            onReady: function (evt) {
              var iframe = evt.target.getIframe();
              if (iframe) iframe.setAttribute('title', title || 'Video');
            },
            onStateChange: function (evt) {
              if (evt.data === YT.PlayerState.ENDED) self.stop();
            }
          }
        });
      });
    }

    stop() {
      if (this.ytPlayer) {
        // destroy() throws if the player never finished initialising.
        if (typeof this.ytPlayer.destroy === 'function') {
          try {
            this.ytPlayer.destroy();
          } catch (e) {
            /* no-op */
          }
        }
        this.ytPlayer = null;
      }

      if (this.video) {
        this.video.pause();
        this.video = null;
      }

      if (this.mount) this.mount.innerHTML = '';
      if (this.stage) this.stage.classList.remove('is-playing');
      // Focus is deliberately not moved here: `stop` also fires on natural end,
      // and stealing focus on a non-user-initiated event is a WCAG 3.2.1 problem.
    }
  }

  if (!customElements.get('video-playlist')) {
    customElements.define('video-playlist', VideoPlaylistEl);
  }
})();
